import { z } from 'zod';
/**
 * Справочник прав. Право — строка `<ресурс>.<действие>`.
 * Роли собираются из этих прав администратором, см. docs/05-auth-rbac.md.
 */
export const PERMISSIONS = [
    'candidates.read',
    'candidates.write',
    'candidates.comment',
    'meetings.read',
    'meetings.write',
    'invites.create',
    'templates.manage',
    'course.read',
    'course.edit',
    'analytics.read',
    'analytics.full',
    'users.manage',
    'roles.manage',
    'settings.manage',
    'audit.read',
];
export const permissionSchema = z.enum(PERMISSIONS);
export const PERMISSION_GROUPS = [
    {
        group: 'Кандидаты',
        permissions: ['candidates.read', 'candidates.write', 'candidates.comment'],
    },
    {
        group: 'Встречи',
        permissions: ['meetings.read', 'meetings.write', 'invites.create', 'templates.manage'],
    },
    { group: 'Курс', permissions: ['course.read', 'course.edit'] },
    { group: 'Аналитика', permissions: ['analytics.read', 'analytics.full'] },
    { group: 'Пользователи', permissions: ['users.manage', 'roles.manage'] },
    { group: 'Система', permissions: ['settings.manage', 'audit.read'] },
];
export const PERMISSION_LABELS = {
    'candidates.read': 'Просмотр кандидатов, истории и экспорта',
    'candidates.write': 'Создание, правка и смена статуса кандидатов',
    'candidates.comment': 'Заметки в карточке кандидата',
    'meetings.read': 'Просмотр встреч, слотов, шаблонов и скриптов',
    'meetings.write': 'Создание встреч и внесение итогов',
    'invites.create': 'Выпуск пригласительных ссылок',
    'templates.manage': 'Правка шаблонов сообщений и скриптов',
    'course.read': 'Просмотр структуры курса',
    'course.edit': 'Редактирование курса и тестов',
    'analytics.read': 'Сводная воронка и графики',
    'analytics.full': 'Срезы по рекрутёрам, причины ухода, план/факт',
    'users.manage': 'Управление аккаунтами',
    'roles.manage': 'Создание ролей и раздача прав',
    'settings.manage': 'Настройки системы и справочники',
    'audit.read': 'Сквозной аудит-лог',
};
/** Коды предустановленных ролей. Администратор может добавлять свои. */
export const SYSTEM_ROLES = [
    'candidate',
    'recruiter',
    'training_lead',
    'head',
    'analyst',
    'admin',
];
export const systemRoleSchema = z.enum(SYSTEM_ROLES);
export const ROLE_LABELS = {
    candidate: 'Соискатель',
    recruiter: 'Рекрутёр',
    training_lead: 'Руководитель обучения',
    head: 'Руководитель',
    analyst: 'Статистика',
    admin: 'Администратор',
};
/**
 * Права предустановленных ролей. Матрица из docs/05-auth-rbac.md.
 *
 * У роли `candidate` прав нет: её маршруты защищены не правами, а
 * принадлежностью — кандидат работает только со своими данными.
 */
export const SYSTEM_ROLE_PERMISSIONS = {
    candidate: ['course.read'],
    recruiter: [
        'meetings.read',
        'meetings.write',
        'invites.create',
        'templates.manage',
        'analytics.read',
    ],
    training_lead: [
        'candidates.read',
        'candidates.write',
        'candidates.comment',
        'course.read',
        'course.edit',
        'analytics.read',
    ],
    head: [
        'candidates.read',
        'candidates.write',
        'candidates.comment',
        'meetings.read',
        'invites.create',
        'analytics.read',
        'analytics.full',
        'audit.read',
    ],
    analyst: ['analytics.read'],
    admin: [...PERMISSIONS],
};
/** Стартовый маршрут кабинета после входа. */
/**
 * Разделы системы и право, которое их открывает.
 *
 * Права шире ролей: у администратора есть и meetings.read, то есть встречи
 * рекрутёра ему доступны — просто попасть туда было неоткуда. Список
 * строится по правам, поэтому и созданная вручную роль получит ровно те
 * разделы, что ей выдали.
 */
export const SECTIONS = [
    { path: '/recruiter', label: 'Встречи', permissions: ['meetings.read'] },
    { path: '/training', label: 'Кандидаты', permissions: ['candidates.read'] },
    { path: '/head', label: 'Сотрудники', permissions: ['analytics.full'] },
    { path: '/stats', label: 'Статистика', permissions: ['analytics.read'] },
    /*
     * Администрирование открывается любым из своих прав, а не одним общим.
     * Иначе выдача права на курс не давала ничего: вкладка есть, а дверь
     * в раздел заперта правом на управление пользователями.
     */
    {
        path: '/admin',
        label: 'Администрирование',
        permissions: ['users.manage', 'roles.manage', 'course.edit', 'settings.manage', 'audit.read'],
    },
];
export const ROLE_HOME = {
    candidate: '/me',
    recruiter: '/recruiter',
    training_lead: '/training',
    head: '/head',
    analyst: '/stats',
    admin: '/admin',
};
//# sourceMappingURL=permissions.js.map